import { useTranslation } from "react-i18next";
import { useTheme, type FontSize, type ThemeMode } from "./ThemeProvider";
import { cx } from "../components/ui/cx";

/**
 * Compact theme + font-size switcher. Designed to live in the top-right of the
 * Layout header. Hidden on very small screens (use settings page instead).
 */
export function ThemeSwitcher({ className }: { className?: string }) {
  const { theme, setTheme, fontSize, setFontSize } = useTheme();
  const { t } = useTranslation("common");

  const themeOpts: { id: ThemeMode; label: string; icon: string }[] = [
    { id: "light",         label: t("theme.light"),        icon: "☀" },
    { id: "dark",          label: t("theme.dark"),         icon: "☾" },
    { id: "high-contrast", label: t("theme.highContrast"), icon: "◐" },
    { id: "system",        label: t("theme.system"),       icon: "⚙" },
  ];
  const fontOpts: { id: FontSize; label: string }[] = [
    { id: "md", label: "A" },
    { id: "lg", label: "A+" },
    { id: "xl", label: "A++" },
    { id: "xxl", label: "A+++" },
  ];

  return (
    <div className={cx("hidden items-center gap-1 sm:flex", className)}>
      <div role="radiogroup" aria-label={t("theme.label")} className="inline-flex overflow-hidden rounded-full border border-white/30 bg-white/10">
        {themeOpts.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={theme === o.id}
            title={o.label}
            onClick={() => setTheme(o.id)}
            className={cx(
              "px-2 py-1 text-xs transition-colors focus-ring",
              theme === o.id ? "bg-white text-[var(--c-brand-strong)]" : "text-white hover:bg-white/15"
            )}
          >
            <span aria-hidden>{o.icon}</span>
            <span className="sr-only">{o.label}</span>
          </button>
        ))}
      </div>
      <div role="radiogroup" aria-label={t("fontSize.label")} className="inline-flex overflow-hidden rounded-full border border-white/30 bg-white/10">
        {fontOpts.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={fontSize === o.id}
            title={`${t("fontSize.label")} — ${o.id}`}
            onClick={() => setFontSize(o.id)}
            className={cx(
              "px-2 py-1 text-[11px] font-semibold transition-colors focus-ring",
              fontSize === o.id ? "bg-white text-[var(--c-brand-strong)]" : "text-white hover:bg-white/15"
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
